<template>
  <div class="time-range-editor">
    <b-dropdown
      v-model="selectedOption"
      expanded
      aria-role="list"
      class="time-range-dropdown"
      :append-to-body="true"
      @active-change="onDropdownToggle">
      <template #trigger="{ active }">
        <button class="button dropdown-button is-fullwidth">
          <div class="mr-auto">
            {{ displayLabel }}
          </div>
          <b-icon :icon="active ? 'menu-up' : 'menu-down'" />
        </button>
      </template>

      <div
        class="dropdown-search"
        @click.stop>
        <div class="search-input-wrapper">
          <i class="material-icons search-icon">search</i>
          <input
            ref="searchInput"
            v-model="searchQuery"
            type="text"
            class="search-input"
            placeholder="Search"
            @keydown.enter.prevent />
        </div>
      </div>

      <b-dropdown-item
        v-if="optional && !searchQuery"
        :value="null"
        aria-role="listitem">
        <span>{{ defaultOptionLabel }}{{ defaultTimeRangeName ? ` (${defaultTimeRangeName})` : '' }}</span>
        <span
          v-if="defaultRangeLabel"
          class="dropdown-item-range">
          {{ defaultRangeLabel }}
        </span>
      </b-dropdown-item>

      <template v-for="(group, gi) in filteredGroupedOptions">
        <hr
          v-if="gi > 0 || (optional && !searchQuery)"
          :key="group.label + '-sep'"
          class="dropdown-divider" />
        <div
          :key="group.label"
          class="dropdown-group-label">
          {{ group.label }}
        </div>
        <b-dropdown-item
          v-for="option in group.options"
          :key="option.key"
          :value="option"
          aria-role="listitem">
          <span>{{ option.name }}</span>
          <span class="dropdown-item-range">
            {{ presetRangeLabel(option.key) }}
          </span>
        </b-dropdown-item>
      </template>

      <template v-if="!searchQuery || 'custom date range'.includes(searchQuery.toLowerCase())">
        <hr class="dropdown-divider" />
        <b-dropdown-item
          :value="RELATIVE_SENTINEL"
          aria-role="listitem">
          Custom Date Range
        </b-dropdown-item>
      </template>
    </b-dropdown>

    <div class="example-box mt-3">
      <div class="example-row">
        <div class="example-row-label">
          <span>Example Report Date</span>
          <b-tooltip
            type="is-primary"
            position="is-right"
            multilined
            size="is-medium">
            <template #content>
              Time ranges are calculated relative to the report date, which
              defaults to the day before a report is sent, but can be
              overwritten in the scheduled report configuration.
            </template>
            <span class="info-icon">
              <i class="material-icons">info</i>
            </span>
          </b-tooltip>
        </div>
        <b-datepicker
          v-model="exampleReportDate"
          placeholder="Pick a date"
          trap-focus
          class="example-datepicker">
          <template #trigger>
            <button class="calendar-btn">
              <span>{{ fmtDate(exampleReportDate) }}</span>
              <i class="material-icons">calendar_today</i>
            </button>
          </template>
        </b-datepicker>
      </div>

      <div
        v-if="isRelative"
        class="example-row mt-3">
        <div class="example-row-label">
          Custom Date Range
        </div>
        <div class="summary-value">
          {{ fmtDate(startDate) }} - {{ fmtDate(endDate) }}
        </div>
        <div class="custom-range-details mt-2">
          <div class="custom-range-info">
            <div>Offset {{ offsetDays }} days</div>
            <div>Duration {{ durationDays }} days</div>
          </div>
          <b-button
            @click="openCalendar">
            Edit
          </b-button>
        </div>
      </div>

      <div
        v-else-if="selectedRangeLabel"
        class="example-row mt-3">
        <div class="example-row-label">
          {{ selectedRangeName }}
        </div>
        <div class="example-row-values">
          <span class="summary-value">{{ selectedRangeLabel }}</span>
        </div>
      </div>
    </div>

    <b-modal
      v-model="calendarOpen"
      :width="580"
      :can-cancel="['escape', 'x']"
      has-modal-card
      trap-focus>
      <div class="modal-card calendar-modal">
        <section class="modal-card-body">
          <div class="calendar-modal-title">
            Custom Date Range
          </div>
          <div class="calendar-layout">
            <div class="calendar-left">
              <b-datepicker
                v-model="pendingDateRange"
                :day-names="['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa',]"
                :events="reportDateEvents"
                indicators="bars"
                range
                inline />
            </div>
            <div class="calendar-right">
              <b-field label="Offset (days)">
                <b-numberinput
                  v-model="pendingOffset"
                  controls-alignment="right"
                  size="is-small" />
              </b-field>
              <b-field label="Duration (days)">
                <b-numberinput
                  v-model="pendingDuration"
                  :min="1"
                  controls-alignment="right"
                  size="is-small" />
              </b-field>
            </div>
          </div>
          <div
            v-if="pendingRangeValid"
            class="date-range-container">
            <div class="custom-date-box">
              {{ fmtDate(pendingDateRange[0]) }}
            </div>
            <div>
              <b-icon
                size="is-small"
                class="arrow-right"
                icon="arrow-right" />
            </div>
            <div class="custom-date-box">
              {{ fmtDate(pendingDateRange[1]) }}
            </div>
          </div>
        </section>
        <footer class="modal-card-foot">
          <b-button
            expanded
            @click="calendarOpen = false">
            Discard
          </b-button>
          <b-button
            expanded
            type="is-primary"
            :disabled="!pendingRangeValid"
            @click="applyDateRange">
            Apply
          </b-button>
        </footer>
      </div>
    </b-modal>
  </div>
</template>

<script>
  import ModuleConfigMixin from '@/components/reports/config/modules/common/ModuleConfigMixin'

  const RELATIVE_SENTINEL = '__relative_date_range__'
  const MS_PER_DAY = 86400000

  function addDays (d, n) {
    const r = new Date(d)
    r.setDate(r.getDate() + n)
    return r
  }

  function startOfWeek (d) {
    const r = new Date(d)
    const day = r.getDay()
    const diff = day === 0 ? 6 : day - 1
    r.setDate(r.getDate() - diff)
    return r
  }

  function startOfMonth (d) {
    return new Date(d.getFullYear(), d.getMonth(), 1)
  }

  function endOfMonth (d) {
    return new Date(d.getFullYear(), d.getMonth() + 1, 0)
  }

  function toDateKey (d) {
    const y = d.getFullYear()
    const m = ('0' + (d.getMonth() + 1)).slice(-2)
    const day = ('0' + d.getDate()).slice(-2)
    return `${y}-${m}-${day}`
  }

  function parseDateKey (key) {
    const [y, m, d,] = key.split('-').map(Number)
    return new Date(y, m - 1, d)
  }

  function fiscalRange (availableDates, reportDateKey, fieldKey) {
    const reportEntry = availableDates.find(d => d.date_key === reportDateKey)
    if (!reportEntry) return null
    const targetKey = reportEntry[fieldKey]
    const yearKey = reportEntry.year_key
    const matches = availableDates.filter(
      d => d.year_key === yearKey && d[fieldKey] === targetKey
    )
    if (!matches.length) return null
    return [parseDateKey(matches[0].date_key), parseDateKey(matches[matches.length - 1].date_key),]
  }

  function fiscalRangeTD (availableDates, reportDateKey, fieldKey) {
    const reportEntry = availableDates.find(d => d.date_key === reportDateKey)
    if (!reportEntry) return null
    const targetKey = reportEntry[fieldKey]
    const yearKey = reportEntry.year_key
    const matches = availableDates.filter(
      d => d.year_key === yearKey && d[fieldKey] === targetKey && d.date_key <= reportDateKey
    )
    if (!matches.length) return null
    return [parseDateKey(matches[0].date_key), parseDateKey(matches[matches.length - 1].date_key),]
  }

  function fiscalYearRange (availableDates, reportDateKey) {
    const reportEntry = availableDates.find(d => d.date_key === reportDateKey)
    if (!reportEntry) return null
    const yearKey = reportEntry.year_key
    const matches = availableDates.filter(d => d.year_key === yearKey)
    if (!matches.length) return null
    return [parseDateKey(matches[0].date_key), parseDateKey(matches[matches.length - 1].date_key),]
  }

  function fiscalYearTD (availableDates, reportDateKey) {
    const reportEntry = availableDates.find(d => d.date_key === reportDateKey)
    if (!reportEntry) return null
    const yearKey = reportEntry.year_key
    const matches = availableDates.filter(
      d => d.year_key === yearKey && d.date_key <= reportDateKey
    )
    if (!matches.length) return null
    return [parseDateKey(matches[0].date_key), parseDateKey(matches[matches.length - 1].date_key),]
  }

  function computePresetRange (key, reportDate, availableDates) {
    const d = new Date(reportDate)
    const dk = toDateKey(d)
    switch (key) {
      case 'current_day':
        return [d, d,]
      case 'previous_day':
        return [addDays(d, -1), addDays(d, -1),]
      case 'current_week':
        return fiscalRange(availableDates, dk, 'week_key') ||
          [startOfWeek(d), addDays(startOfWeek(d), 6),]
      case 'wtd':
        return fiscalRangeTD(availableDates, dk, 'week_key') ||
          [startOfWeek(d), d,]
      case 'previous_week': {
        const prevDay = addDays(startOfWeek(d), -1)
        const prevDk = toDateKey(prevDay)
        return fiscalRange(availableDates, prevDk, 'week_key') ||
          (() => { const ps = addDays(startOfWeek(d), -7); return [ps, addDays(ps, 6),] })()
      }
      case 'trailing_7':
        return [addDays(d, -6), d,]
      case 'next_week': {
        const nextDay = addDays(startOfWeek(d), 7)
        const nextDk = toDateKey(nextDay)
        return fiscalRange(availableDates, nextDk, 'week_key') ||
          [nextDay, addDays(nextDay, 6),]
      }
      case 'current_month':
        return [startOfMonth(d), endOfMonth(d),]
      case 'mtd':
        return [startOfMonth(d), d,]
      case 'previous_month': {
        const prevMonth = new Date(d.getFullYear(), d.getMonth() - 1, 1)
        return [prevMonth, endOfMonth(prevMonth),]
      }
      case 'current_period':
        return fiscalRange(availableDates, dk, 'period_key')
      case 'ptd':
        return fiscalRangeTD(availableDates, dk, 'period_key')
      case 'current_quarter':
        return fiscalRange(availableDates, dk, 'quarter_key')
      case 'qtd':
        return fiscalRangeTD(availableDates, dk, 'quarter_key')
      case 'current_calendar_year':
        return [new Date(d.getFullYear(), 0, 1), new Date(d.getFullYear(), 11, 31),]
      case 'calendar_ytd':
        return [new Date(d.getFullYear(), 0, 1), d,]
      case 'current_year':
        return fiscalYearRange(availableDates, dk)
      case 'ytd':
        return fiscalYearTD(availableDates, dk)
      case 'all_time': {
        if (!availableDates.length) return null
        return [
          parseDateKey(availableDates[0].date_key),
          parseDateKey(availableDates[availableDates.length - 1].date_key),
        ]
      }
      default:
        return null
    }
  }

  export default {
    name: 'TimeRangeEditor',
    mixins: [
      ModuleConfigMixin,
    ],
    props: {
      optional: {
        type: Boolean,
        default: true,
      },
      defaultTimeRangeName: {
        type: String,
        default: '',
      },
      defaultOptionLabel: {
        type: String,
        default: 'Use report default',
      },
      defaultTimeRange: {
        type: Object,
        default: null,
      },
    },
    data () {
      const yesterday = new Date()
      yesterday.setDate(yesterday.getDate() - 1)
      yesterday.setHours(0, 0, 0, 0)
      return {
        defaultValue: null,
        RELATIVE_SENTINEL,
        calendarOpen: false,
        pendingDateRange: [],
        exampleReportDate: yesterday,
        searchQuery: '',
      }
    },
    computed: {
      fiscalLoading () {
        return this.$store?.state?.datePicker?.loading || false
      },
      availableDates () {
        return this.$store?.getters['datePicker/availableDates'] || []
      },
      groupedOptions () {
        return [
          {
            label: 'Daily',
            options: this.timeRangeOptions.filter((o) =>
              ['current_day', 'previous_day',].includes(o.key)
            ),
          },
          {
            label: 'Weekly',
            options: this.timeRangeOptions.filter((o) =>
              ['current_week', 'wtd', 'previous_week', 'trailing_7', 'next_week',].includes(o.key)
            ),
          },
          {
            label: 'Monthly / Period',
            options: this.timeRangeOptions.filter((o) =>
              ['current_month', 'mtd', 'previous_month', 'current_period', 'ptd',].includes(o.key)
            ),
          },
          {
            label: 'Quarterly / Yearly',
            options: this.timeRangeOptions.filter((o) =>
              ['current_quarter', 'qtd', 'current_calendar_year', 'calendar_ytd',
               'current_year', 'ytd', 'all_time',].includes(o.key)
            ),
          },
        ]
      },

      filteredGroupedOptions () {
        if (!this.searchQuery) return this.groupedOptions
        const q = this.searchQuery.toLowerCase()
        return this.groupedOptions
          .map(group => ({
            ...group,
            options: group.options.filter(o => o.name.toLowerCase().includes(q) || o.key.toLowerCase().includes(q)),
          }))
          .filter(group => group.options.length > 0)
      },

      isRelative () {
        return this.value?.type === 'RelativeDateRange'
      },

      displayLabel () {
        if (this.value === null || this.value === undefined) {
          return this.optional
            ? (this.defaultOptionLabel + (this.defaultTimeRangeName ? ` (${this.defaultTimeRangeName})` : ''))
            : 'Select time range'
        }
        if (this.isRelative) {
          return 'Custom Date Range'
        }
        return this.value?.name || this.value?.key || 'Select time range'
      },

      selectedOption: {
        get () {
          if (this.isRelative) return RELATIVE_SENTINEL
          return this.value
        },
        set (value) {
          if (value === RELATIVE_SENTINEL) {
            this.$emit('input', {
              type: 'RelativeDateRange',
              offset_days: 0,
              duration_days: 7,
            })
          } else {
            this.$emit('input', value)
          }
        },
      },

      durationDays: {
        get () {
          return this.value?.duration_days || 0
        },
        set (duration_days) {
          this.$emit('input', {
            ...this.value,
            duration_days,
          })
        },
      },

      offsetDays: {
        get () {
          return this.value?.offset_days || 0
        },
        set (offset_days) {
          this.$emit('input', {
            ...this.value,
            offset_days,
          })
        },
      },

      reportDateEvents () {
        return [{ date: this.exampleReportDate, type: 'is-primary', },]
      },
      pendingOffset: {
        get () {
          if (!this.pendingRangeValid) return 0
          return Math.round((this.pendingDateRange[1] - this.exampleReportDate) / MS_PER_DAY)
        },
        set (val) {
          const end = addDays(this.exampleReportDate, val || 0)
          const dur = this.pendingDuration || 7
          const start = addDays(end, -(dur - 1))
          this.pendingDateRange = [start, end,]
        },
      },
      pendingDuration: {
        get () {
          if (!this.pendingRangeValid) return 7
          return Math.round((this.pendingDateRange[1] - this.pendingDateRange[0]) / MS_PER_DAY) + 1
        },
        set (val) {
          const dur = Math.max(1, val || 1)
          const end = this.pendingRangeValid
            ? this.pendingDateRange[1]
            : this.exampleReportDate
          const start = addDays(end, -(dur - 1))
          this.pendingDateRange = [start, end,]
        },
      },
      pendingRangeValid () {
        return this.pendingDateRange &&
          this.pendingDateRange.length === 2 &&
          this.pendingDateRange[0] &&
          this.pendingDateRange[1]
      },

      effectiveOffsetDays () {
        return this.effectiveTimeRange?.offset_days || 0
      },
      effectiveDurationDays () {
        return this.effectiveTimeRange?.duration_days || 0
      },
      endDate () {
        const d = new Date(this.exampleReportDate)
        d.setDate(d.getDate() + (this.effectiveOffsetDays))
        return d
      },
      startDate () {
        const d = new Date(this.endDate)
        d.setDate(d.getDate() - (this.effectiveDurationDays) + 1)
        return d
      },
      defaultRangeLabel () {
        if (!this.defaultTimeRange) return null
        if (this.defaultTimeRange.key) {
          return this.presetRangeLabel(this.defaultTimeRange.key)
        }
        return null
      },
      effectiveTimeRange () {
        if (this.value !== null && this.value !== undefined) return this.value
        return this.defaultTimeRange
      },
      selectedRangeName () {
        if (!this.effectiveTimeRange) return ''
        if (this.effectiveTimeRange.type === 'RelativeDateRange') return 'Custom Date Range'
        return this.effectiveTimeRange.name || this.effectiveTimeRange.key || ''
      },
      selectedRangeLabel () {
        if (!this.effectiveTimeRange) return null
        if (this.effectiveTimeRange.type === 'RelativeDateRange') {
          return this.fmtDate(this.startDate) + ' - ' + this.fmtDate(this.endDate)
        }
        if (this.effectiveTimeRange.key) {
          return this.presetRangeLabel(this.effectiveTimeRange.key)
        }
        return null
      },
    },
    watch: {
      exampleReportDate (val) {
        this.$emit('example-date-change', val)
      },
    },
    async mounted () {
      this.$emit('example-date-change', this.exampleReportDate)
      await this.$store.dispatch('datePicker/initialize')
    },
    methods: {
      fmtDate (d) {
        if (!d) return ''
        return d.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      },
      fmtShort (d) {
        if (!d) return ''
        return d.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        })
      },
      presetRangeLabel (key) {
        const range = computePresetRange(key, this.exampleReportDate, this.availableDates)
        if (!range) {
          return this.fiscalLoading ? 'Loading fiscal calendar...' : 'Fiscal calendar unavailable'
        }
        return this.fmtShort(range[0]) + ' - ' + this.fmtShort(range[1])
      },
      onDropdownToggle (isOpen) {
        if (isOpen) {
          this.searchQuery = ''
          this.$nextTick(() => {
            this.$refs.searchInput?.focus()
          })
        }
      },
      openCalendar () {
        this.pendingDateRange = [this.startDate, this.endDate,]
        this.calendarOpen = true
      },
      applyDateRange () {
        if (!this.pendingRangeValid) return
        const start = this.pendingDateRange[0]
        const end = this.pendingDateRange[1]
        const duration_days = Math.round((end - start) / MS_PER_DAY) + 1
        const offset_days = Math.round((end - this.exampleReportDate) / MS_PER_DAY)
        this.$emit('input', {
          type: 'RelativeDateRange',
          offset_days,
          duration_days: Math.max(1, duration_days),
        })
        this.calendarOpen = false
      },
    },
  }
</script>

<style lang="scss" scoped>
.info-icon {
  cursor: help;
  display: inline-flex;
  align-items: center;

  .material-icons {
    font-size: 16px;
    color: #888;
  }
}

.dropdown-group-label {
  padding: 4px 16px 2px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: #999;
  letter-spacing: 0.5px;
}

.dropdown-search {
  padding: 8px 12px;
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);

  .search-input-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .search-icon {
    font-size: 18px;
    color: #999;
    flex-shrink: 0;
  }

  .search-input {
    flex: 1;
    border: none;
    outline: none;
    font-size: 14px;
    line-height: 20px;
    background: transparent;
    min-width: 0;

    &::placeholder {
      color: #999;
    }
  }
}

.time-range-dropdown ::v-deep .dropdown-menu {
  background: white;
}

.time-range-dropdown ::v-deep .dropdown-content {
  max-height: 350px;
  overflow-y: auto;
}

::v-deep .dropdown-item {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .dropdown-item-range {
    font-size: 11px;
    color: #999;
    margin-left: 12px;
    white-space: nowrap;
  }
}

.relative-config {
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 12px;
  background: #fafafa;
}

.relative-summary-row {
  display: flex;
  align-items: center;
  gap: 12px;

  .summary-item {
    .summary-label {
      display: block;
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      color: #999;
      letter-spacing: 0.5px;
    }

    .summary-value {
      font-size: 13px;
      font-weight: 500;
      color: #333;
    }
  }

  .button {
    margin-left: auto;
  }
}

.calendar-modal {
  background: white;
  border-radius: 8px;

  .calendar-modal-title {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 12px;
  }

  .calendar-layout {
    display: flex;
    gap: 16px;

    .calendar-left {
      flex: 0 0 auto;
    }

    .calendar-right {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 12px;
    }
  }

  .modal-card-body {
    padding: 16px;

    ::v-deep .datepicker {
      .dropdown-content {
        box-shadow: none;
        padding: 0;
        width: fit-content;
      }

      .dropdown-menu {
        width: fit-content;
        min-width: 0;
      }

      .datepicker-cell.is-today {
        border: none !important;
        border-color: transparent !important;
      }

      .datepicker-cell.is-within-hovered-range:not(.is-selected):not(.is-first-hovered):not(.is-last-hovered) {
        background-color: transparent !important;
        color: inherit !important;
      }

      .datepicker-cell:not(.is-selected):not(.is-unselectable):hover {
        background-color: rgba(0, 0, 0, 0.05);
        border-radius: 4px;
      }

      .datepicker-cell .events .event.is-primary {
        width: 100%;
        height: 2px;
      }
    }
  }

  .modal-card-foot {
    background: $grayscale-8;
    display: flex;
    padding: 16px;
    gap: 8px;
  }

  .date-range-container {
    padding-top: 16px;
    border-top: $border-1;
    display: flex;
    justify-content: center;
    align-items: center;

    .custom-date-box {
      font-size: 14px;
      border: $border-1;
      border-radius: 4px;
      height: 38px;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
    }

    .arrow-right {
      font-size: 20px;
      margin: 3px 15px;
      color: $grayscale-4;
    }
  }
}

.example-box {
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 12px;
  background: #fafafa;
}

.example-row {
  .example-row-label {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    font-weight: 500;
    color: #666;
    margin-bottom: 4px;
  }

  .example-row-values {
    display: flex;
    align-items: center;
    gap: 12px;
  }
}

.custom-range-details {
  display: flex;
  align-items: center;

  .custom-range-info {
    font-size: 12px;
    color: #888;
    line-height: 1.5;
  }

  .button {
    margin-left: auto;
  }
}

.example-row + .example-row {
  padding-top: 10px;
  border-top: 1px solid #eee;
}

.calendar-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  padding: 6px 10px;
  font-size: 13px;
  color: #333;

  .material-icons {
    font-size: 18px;
    color: #666;
  }

  &:hover {
    background: #f5f5f5;
    border-color: #ccc;
  }
}

</style>
